import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, Platform, StyleSheet } from 'react-native';
import { IconButton } from 'react-native-paper';
import { SingleSelectDropdown } from './Dropdown-filter';

const PRESET_OPTIONS = [
  { label: 'All Time',          value: 'ALL' },
  { label: 'This Month',        value: 'THIS_MONTH' },
  { label: 'Last Month',        value: 'LAST_MONTH' },
  { label: 'Last 3 Months',     value: 'LAST_3M' },
  { label: 'Last 6 Months',     value: 'LAST_6M' },
  { label: 'This Year',         value: 'THIS_YEAR' },
  { label: 'Last Year',         value: 'LAST_YEAR' },
  { label: 'Custom Date Range', value: 'CUSTOM' },
];

function presetToDates(preset: string): [Date, Date] {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  switch (preset) {
    case 'THIS_MONTH':
      return [
        new Date(today.getFullYear(), today.getMonth(), 1),
        new Date(today.getFullYear(), today.getMonth() + 1, 0),
      ];
    case 'LAST_MONTH':
      return [
        new Date(today.getFullYear(), today.getMonth() - 1, 1),
        new Date(today.getFullYear(), today.getMonth(), 0),
      ];
    case 'LAST_3M': {
      const s = new Date(today); s.setDate(s.getDate() - 90); return [s, today];
    }
    case 'LAST_6M': {
      const s = new Date(today); s.setDate(s.getDate() - 180); return [s, today];
    }
    case 'THIS_YEAR':
      return [new Date(today.getFullYear(), 0, 1), new Date(today.getFullYear(), 11, 31)];
    case 'LAST_YEAR':
      return [new Date(today.getFullYear() - 1, 0, 1), new Date(today.getFullYear() - 1, 11, 31)];
    default:
      return [today, today];
  }
}

// Date ↔ YYYY-MM-DD (the value format required by <input type="date">)
function toInputValue(d: Date | null): string {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function fromInputValue(s: string): Date | null {
  if (!s) return null;
  const [y, m, d] = s.split('-');
  return new Date(+y, +m - 1, +d);
}

function parseDDMMYYYY(s: string): Date {
  const [d, m, y] = s.split('/');
  return new Date(+y, +m - 1, +d);
}

interface LogDateRangeFilterProps {
  data: any[];
  dateField?: string;
  onFilterChange: (filtered: any[]) => void;
  onRangeChange?: (start: Date | null, end: Date | null) => void;
  defaultPreset?: string;
  panelAlign?: 'left' | 'right';
}

function getPresetDates(preset: string): [Date | null, Date | null] {
  if (preset && preset !== 'ALL' && preset !== 'CUSTOM') {
    return presetToDates(preset);
  }
  return [null, null];
}

export default function LogDateRangeFilter({
  data,
  dateField = 'Date',
  onFilterChange,
  onRangeChange,
  defaultPreset = 'ALL',
  panelAlign = 'right',
}: LogDateRangeFilterProps) {
  const [showPanel, setShowPanel] = useState(false);
  const [selected, setSelected]   = useState(() => defaultPreset);
  const [startDate, setStartDate] = useState<Date | null>(() => getPresetDates(defaultPreset)[0]);
  const [endDate, setEndDate]     = useState<Date | null>(() => getPresetDates(defaultPreset)[1]);
  const [dateError, setDateError] = useState<string | null>(null);

  useEffect(() => {
    const [resetStart, resetEnd] = getPresetDates(defaultPreset);
    setSelected(defaultPreset);
    setStartDate(resetStart);
    setEndDate(resetEnd);
    onFilterChange(data);
    onRangeChange?.(resetStart, resetEnd);
  }, [data]);

  function applyFilter(s: Date | null, e: Date | null) {
    onRangeChange?.(s, e);
    if (!s && !e) { onFilterChange(data); return; }
    const start = s ? new Date(s.getFullYear(), s.getMonth(), s.getDate(), 0, 0, 0) : null;
    const end   = e ? new Date(e.getFullYear(), e.getMonth(), e.getDate(), 23, 59, 59) : null;
    onFilterChange(
      data.filter(item => {
        if (!item[dateField]) return false;
        const d = parseDDMMYYYY(item[dateField]);
        if (start && d < start) return false;
        if (end   && d > end)   return false;
        return true;
      }),
    );
  }

  function handlePresetSelect(val: string) {
    setSelected(val);
    if (val === 'ALL') {
      setStartDate(null);
      setEndDate(null);
      onFilterChange(data);
      onRangeChange?.(null, null);
      setShowPanel(false);
      return;
    }
    if (val === 'CUSTOM') {
      return;
    }
    const [s, e] = presetToDates(val);
    setStartDate(s);
    setEndDate(e);
    applyFilter(s, e);
    setShowPanel(false);
  }

  const activeLabel = selected !== 'ALL'
    ? PRESET_OPTIONS.find(o => o.value === selected)?.label
    : null;

  return (
    <View style={styles.wrapper}>
      {/* Trigger */}
      <TouchableOpacity
        onPress={() => setShowPanel(v => !v)}
        style={styles.trigger}
        activeOpacity={0.75}
      >
        <Text style={styles.triggerLabel}>Filter by date</Text>
        <IconButton icon="filter-outline" size={15} iconColor="#4BB75F" style={styles.triggerIcon} />
        {activeLabel && (
          <View style={styles.chip}>
            <Text style={styles.chipText}>{activeLabel}</Text>
          </View>
        )}
      </TouchableOpacity>

      {/* Panel */}
      {showPanel && (
        <View style={[styles.panel, panelAlign === 'left' ? { left: 0, right: undefined } : { right: 0, left: undefined }]}>
          <View style={styles.panelHeader}>
            <Text style={styles.panelTitle}>Date Filter</Text>
            <IconButton
              icon="close"
              size={16}
              iconColor="#666"
              style={styles.closeIcon}
              onPress={() => setShowPanel(false)}
            />
          </View>

          {/* Preset dropdown */}
          <View style={styles.presetRow}>
            <SingleSelectDropdown
              options={PRESET_OPTIONS}
              label="Select range"
              selected={selected}
              onSelect={handlePresetSelect}
            />
          </View>

          {/* Native date inputs — open as OS browser overlay, no z-index conflict */}
          <View style={styles.datesRow}>
            <View style={styles.dateCol}>
              <Text style={styles.dateLabel}>Start Date</Text>
              {Platform.OS === 'web' && (
                <input
                  type="date"
                  value={toInputValue(startDate)}
                  onChange={e => {
                    const d = fromInputValue(e.target.value);
                    setStartDate(d);
                    setDateError(d && endDate && d > endDate ? 'Start date cannot be after end date.' : null);
                  }}
                  style={nativeDateInputStyle}
                />
              )}
            </View>
            <View style={styles.dateCol}>
              <Text style={styles.dateLabel}>End Date</Text>
              {Platform.OS === 'web' && (
                <input
                  type="date"
                  value={toInputValue(endDate)}
                  onChange={e => {
                    const d = fromInputValue(e.target.value);
                    setEndDate(d);
                    setDateError(startDate && d && startDate > d ? 'Start date cannot be after end date.' : null);
                  }}
                  style={nativeDateInputStyle}
                />
              )}
            </View>
          </View>
          {dateError && (
            <Text style={styles.errorText}>{dateError}</Text>
          )}

          {/* Apply */}
          <TouchableOpacity
            style={[styles.applyBtn, dateError ? styles.applyBtnDisabled : null]}
            onPress={() => {
              if (dateError) return;
              setSelected('CUSTOM');
              applyFilter(startDate, endDate);
              setShowPanel(false);
            }}
          >
            <Text style={styles.applyBtnText}>Apply</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
}

// Inline style object for the native <input type="date"> — not a RN StyleSheet
const nativeDateInputStyle: React.CSSProperties = {
  width: '100%',
  border: '1px solid #ddd',
  borderRadius: 6,
  padding: '8px 10px',
  fontSize: 13,
  backgroundColor: '#fafafa',
  color: '#333',
  cursor: 'pointer',
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'Inter, system-ui, sans-serif',
};

const styles = StyleSheet.create({
  wrapper: {
    position: 'relative' as any,
    zIndex: 200,
  },
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 6,
    shadowColor: '#000',
    shadowOpacity: 0.08,
    shadowRadius: 4,
    shadowOffset: { width: 0, height: 2 },
    elevation: 2,
    gap: 4,
  },
  triggerLabel: {
    fontSize: 13,
    fontWeight: '600',
    color: '#333',
    fontFamily: 'Inter, system-ui, sans-serif',
  },
  triggerIcon: {
    margin: 0,
    padding: 0,
  },
  chip: {
    backgroundColor: '#E8F5E9',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  chipText: {
    fontSize: 11,
    color: '#2E7D32',
    fontWeight: '600',
    fontFamily: 'Inter, system-ui, sans-serif',
  },
  panel: {
    position: 'absolute' as any,
    top: 44,
    right: 0,
    zIndex: 999,
    backgroundColor: '#fff',
    borderRadius: 10,
    padding: 16,
    minWidth: 360,
    shadowColor: '#000',
    shadowOpacity: 0.18,
    shadowRadius: 14,
    shadowOffset: { width: 0, height: 6 },
    elevation: 16,
  },
  panelHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  panelTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#333',
    fontFamily: 'Inter, system-ui, sans-serif',
  },
  closeIcon: {
    margin: 0,
    padding: 0,
  },
  presetRow: {
    marginBottom: 14,
    zIndex: 300 as any,
  },
  datesRow: {
    flexDirection: 'row',
    gap: 12,
    marginBottom: 16,
  },
  dateCol: {
    flex: 1,
  },
  dateLabel: {
    fontSize: 11,
    color: '#666',
    fontWeight: '600',
    marginBottom: 6,
    fontFamily: 'Inter, system-ui, sans-serif',
  },
  applyBtn: {
    backgroundColor: '#4BB75F',
    borderRadius: 6,
    paddingVertical: 10,
    alignItems: 'center',
  },
  applyBtnText: {
    color: '#fff',
    fontWeight: '700',
    fontSize: 14,
    fontFamily: 'Inter, system-ui, sans-serif',
  },
  applyBtnDisabled: {
    backgroundColor: '#A5D6A7',
  },
  errorText: {
    fontSize: 11,
    color: '#C62828',
    marginBottom: 10,
    fontFamily: 'Inter, system-ui, sans-serif',
  },
});
